import { GAME_CONFIG } from '../config';
import { type ShapeMatrix } from './Piece';

export type GridCell = number; // 0 = empty, >0 = color index
export type Grid = GridCell[][];

export class Board {
  public grid: Grid;

  constructor() {
    this.grid = this.createEmptyGrid();
  }

  private createEmptyGrid(): Grid {
    const { rows, cols } = GAME_CONFIG.grid;
    const grid: Grid = [];
    for (let r = 0; r < rows; r++) {
      grid.push(new Array(cols).fill(0));
    }
    return grid;
  }

  /**
   * Reset the board to empty.
   */
  reset(): void {
    this.grid = this.createEmptyGrid();
  }

  /**
   * Check if a piece can be placed at (row, col) on the grid.
   * The piece shape is a 0/1 matrix where 1 = occupied cell.
   */
  canPlace(shape: ShapeMatrix, row: number, col: number): boolean {
    const { rows, cols } = GAME_CONFIG.grid;
    for (let r = 0; r < shape.length; r++) {
      for (let c = 0; c < shape[r].length; c++) {
        if (shape[r][c] === 0) continue;
        const gridR = row + r;
        const gridC = col + c;
        // Out of bounds
        if (gridR < 0 || gridR >= rows || gridC < 0 || gridC >= cols) {
          return false;
        }
        // Cell already occupied
        if (this.grid[gridR][gridC] !== 0) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Place a piece at (row, col) on the grid.
   * Assumes canPlace has already been checked.
   */
  placePiece(shape: ShapeMatrix, row: number, col: number, colorIndex: number): void {
    for (let r = 0; r < shape.length; r++) {
      for (let c = 0; c < shape[r].length; c++) {
        if (shape[r][c] === 0) continue;
        this.grid[row + r][col + c] = colorIndex;
      }
    }
  }

  /**
   * Check for completed rows and columns.
   * Returns arrays of completed row indices and column indices.
   */
  checkLines(): { rows: number[]; cols: number[] } {
    const { rows, cols } = GAME_CONFIG.grid;
    const completedRows: number[] = [];
    const completedCols: number[] = [];

    // Check rows
    for (let r = 0; r < rows; r++) {
      if (this.grid[r].every((cell) => cell !== 0)) {
        completedRows.push(r);
      }
    }

    // Check columns
    for (let c = 0; c < cols; c++) {
      let full = true;
      for (let r = 0; r < rows; r++) {
        if (this.grid[r][c] === 0) {
          full = false;
          break;
        }
      }
      if (full) {
        completedCols.push(c);
      }
    }

    return { rows: completedRows, cols: completedCols };
  }

  /**
   * Clear completed lines (rows and columns).
   * Returns the total number of cells cleared.
   */
  clearLines(lines: { rows: number[]; cols: number[] }): number {
    const { rows, cols } = GAME_CONFIG.grid;
    let cleared = 0;

    // Clear completed rows
    for (const r of lines.rows) {
      for (let c = 0; c < cols; c++) {
        if (this.grid[r][c] !== 0) {
          this.grid[r][c] = 0;
          cleared++;
        }
      }
    }

    // Clear completed columns
    for (const c of lines.cols) {
      for (let r = 0; r < rows; r++) {
        if (this.grid[r][c] !== 0) {
          this.grid[r][c] = 0;
          cleared++;
        }
      }
    }

    return cleared;
  }

  /**
   * Check if the board is completely empty.
   */
  isBoardEmpty(): boolean {
    return this.grid.every((row) => row.every((cell) => cell === 0));
  }

  /**
   * Check if game is over: none of the given pieces can be placed anywhere.
   */
  isGameOver(shapes: ShapeMatrix[]): boolean {
    const { rows, cols } = GAME_CONFIG.grid;
    for (const shape of shapes) {
      for (let r = 0; r <= rows - shape.length; r++) {
        for (let c = 0; c <= cols - shape[0].length; c++) {
          if (this.canPlace(shape, r, c)) {
            return false;
          }
        }
      }
    }
    return true;
  }
}